/**
 * The play as the field draws it, built only from the sim's event log. Every position here is one
 * an event names, and between two of them a sprite moves in a straight line at a steady speed.
 * Nothing in this file decides anything (CLAUDE.md rule 2); it has no Phaser import so it can be
 * tested in Vitest.
 */
import type { PlayEvent, Spot, UnitId } from '@tdmons/sim';

/** A position at a time. A track is keys in time order; between two keys the sprite moves linearly. */
export interface Key {
  t: number;
  at: Spot;
}
export type Track = readonly Key[];

/** Who or where the ball is, from a time on. */
export type BallPhase =
  | { t: number; kind: 'held'; unit: UnitId }
  | { t: number; kind: 'flight'; from: Spot; to: Spot; arrives: number }
  | { t: number; kind: 'loose'; at: Spot };

export interface Timeline {
  units: ReadonlyMap<UnitId, Track>;
  ball: readonly BallPhase[];
  /** The whistle. */
  end: number;
}

type Of<T extends PlayEvent['type']> = Extract<PlayEvent, { type: T }>;

export function buildTimeline(events: readonly PlayEvent[]): Timeline {
  const snap = events[0];
  if (snap?.type !== 'snap') throw new Error('an event log starts with the snap');
  const units = new Map<UnitId, Key[]>();
  for (const [unit, at] of Object.entries(snap.formation) as [UnitId, Spot][]) {
    units.set(unit, [{ t: 0, at }]);
  }
  for (const e of events) {
    if (e.type !== 'move') continue;
    const keys = units.get(e.unit);
    if (!keys) throw new Error(`${e.unit} moves but was not in the formation`);
    keys.push({ t: e.t, at: e.from }, { t: e.t + e.duration, at: e.to });
  }

  const ball: BallPhase[] = [{ t: 0, kind: 'held', unit: snap.ball }];
  const tracks: ReadonlyMap<UnitId, Track> = units;
  for (const e of events) {
    switch (e.type) {
      case 'throw':
      case 'throw-away': {
        const holder = ballAt(ball, tracks, e.t);
        ball.push({ t: e.t, kind: 'flight', from: holder, to: e.at, arrives: e.arrives });
        ball.push({ t: e.arrives, kind: 'loose', at: e.at });
        break;
      }
      case 'handoff':
        ball.push({ t: e.t, kind: 'held', unit: e.to });
        break;
      case 'catch':
      case 'interception':
        ball.push({ t: e.t, kind: 'held', unit: e.unit });
        break;
      case 'fumble':
        ball.push({ t: e.t, kind: 'loose', at: e.at });
        break;
      default:
        break;
    }
  }
  // A formation alone (slice 8b, between plays) is the snap event and nothing else; it ends at 0.
  if (events.length === 1) return { units: tracks, ball, end: 0 };
  const whistle = events.at(-1) as Of<'whistle'>;
  if (whistle.type !== 'whistle') throw new Error('an event log ends with the whistle');
  return { units: tracks, ball, end: whistle.t };
}

/** Where a track puts its sprite at time t. Before the first key it is at the first key's spot. */
export function positionAt(track: Track, t: number): Spot {
  const first = track[0];
  if (!first) throw new Error('empty track');
  if (t <= first.t) return first.at;
  for (let i = 1; i < track.length; i++) {
    const k1 = track[i] as Key;
    if (t > k1.t) continue;
    const k0 = track[i - 1] as Key;
    if (k1.t === k0.t) return k1.at;
    const f = (t - k0.t) / (k1.t - k0.t);
    return [k0.at[0] + (k1.at[0] - k0.at[0]) * f, k0.at[1] + (k1.at[1] - k0.at[1]) * f];
  }
  return (track.at(-1) as Key).at;
}

/** The phase the ball is in at time t: the last one that has started. */
export function ballPhaseAt(ball: readonly BallPhase[], t: number): BallPhase {
  let phase = ball[0] as BallPhase;
  for (const p of ball) {
    if (p.t > t) break;
    phase = p;
  }
  return phase;
}

/** The ball's spot on the ground at time t (flight height is a drawing concern, not a spot). */
export function ballAt(
  ball: readonly BallPhase[],
  units: ReadonlyMap<UnitId, Track>,
  t: number,
): Spot {
  const p = ballPhaseAt(ball, t);
  if (p.kind === 'loose') return p.at;
  if (p.kind === 'held') return positionAt(units.get(p.unit) as Track, t);
  const f = Math.min(1, (t - p.t) / (p.arrives - p.t));
  return [p.from[0] + (p.to[0] - p.from[0]) * f, p.from[1] + (p.to[1] - p.from[1]) * f];
}

/** How far through a throw the ball is at time t, and how long the throw is; null on the ground. */
export function flightAt(
  ball: readonly BallPhase[],
  t: number,
): { f: number; yards: number } | null {
  const p = ballPhaseAt(ball, t);
  if (p.kind !== 'flight') return null;
  return {
    f: Math.min(1, (t - p.t) / (p.arrives - p.t)),
    yards: Math.hypot(p.to[0] - p.from[0], p.to[1] - p.from[1]),
  };
}

/**
 * Hands out the log's events as playback reaches their time: each one exactly once, in log order,
 * however large the jump (a skip consumes everything that is left).
 */
export class EventCursor {
  private next = 0;

  constructor(private readonly events: readonly PlayEvent[]) {}

  advance(t: number): PlayEvent[] {
    const out: PlayEvent[] = [];
    while (this.next < this.events.length && (this.events[this.next] as PlayEvent).t <= t) {
      out.push(this.events[this.next++] as PlayEvent);
    }
    return out;
  }

  /** Index in the log of the next event advance() will hand out. */
  get position(): number {
    return this.next;
  }

  get done(): boolean {
    return this.next >= this.events.length;
  }
}
